<script setup>
  import { computed, inject } from 'vue'
  import { useDisplay } from 'vuetify'
  import { useI18n } from 'vue-i18n'
  import StandingsField from '../TournamentForm/StandingsField.vue'
  import EntryName from '@/components/EntryName.vue'
  import { Config } from '@/Config.js'
import TeamName from '@/components/TeamName.vue'

  const tournament = inject('tournament')
  const editMode = inject('editMode')

  defineEmits(['remove-from-group'])

  const { t } = useI18n()
  const { mobile } = useDisplay()

  const group_entries = defineModel('group_entries', { type: Array, default: () => [] })
  const props = defineProps({
    fullDataEntries: { type: Array, default: () => [] },
    standingsType: { type: String, default: '' }
  })

  const columns = computed(() => {
    const tournamentSettings = tournament.value.settings || {}
    let standingsColumns = tournamentSettings.standingsColumns || null

    if (!standingsColumns) {
      standingsColumns = Config.tournament.standingsColumns
        .filter(col => col.enabled)
        .map(col => col.key)
    }

    const baseColumns = []
    if (!mobile.value) {
      baseColumns.push({ title: '', key: 'position', sticky: true })
    }
    baseColumns.push({ title: '', key: 'name', sticky: true })

    const dynamicColumns = standingsColumns.map(columnKey => {
      return { title: t('standingsColumns.' + columnKey), key: columnKey }
    })

    return [...baseColumns, ...dynamicColumns]
  })

  const isColumnsEditable = computed(() => {
    if (!editMode.value || props.standingsType == 'teams') {
      return () => false
    }
    return (fieldName) => {
      return fieldName !== 'position' && fieldName !== 'name'
    }
  })

  const updateEntryColumn = (entry, columnKey, value) => {
    const index = group_entries.value.findIndex(e => e.entry_id === entry.entry_id)
    if (index !== -1) {
      group_entries.value[index][columnKey] = value
    }
  }
</script>

<template>
  <div v-if="fullDataEntries && fullDataEntries.length > 0" class="table-container">
    <v-table class="standings-table">
      <thead>
        <tr>
          <th
            v-for="(column, colIndex) in columns"
            :key="column.key"
            :class="['text-left', {
                'sticky-column': column.sticky,
                'sticky-column-first': column.key === 'position',
                'sticky-column-second': column.key === 'name',
                'sticky-column-second--desktop': column.key === 'name' && !mobile,
                'text-center': column.key !== 'position' && column.key !== 'name'
              }]"
          >
            {{ column.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(entry, index) in fullDataEntries" :key="entry.id || index">
          <td
            v-for="(column, colIndex) in columns"
            :key="column.key"
            :class="{
              'sticky-column': column.sticky,
              'sticky-column-first': column.key === 'position',
              'sticky-column-second': column.key === 'name',
              'sticky-column-second--desktop': column.key === 'name' && !mobile,
              'text-center': column.key !== 'position' && column.key !== 'name'
            }"
          >
            <!-- Position -->
            <template v-if="column.key === 'position'">
              {{ index + 1 }}
            </template>

            <!-- Name -->
            <div v-else-if="column.key === 'name'" class="d-flex
              justify-sm-space-between
              flex-column flex-sm-row
              align-start align-sm-center
              ga-sm-2
              
            ">
              <div>
                <TeamName v-if="standingsType == 'teams'" :teamId="entry.team_id" :withFlag="true" />
                <EntryName v-else :entryId="entry.entry_id" />
              </div>
              <div class="d-flex align-center ga-4 ms-sm-2">
                <!-- Team -->
                <div v-if="column.key === 'name' && 
                  tournament.settings?.useTeams &&
                  standingsType != 'teams' &&
                  entry.team_id">
                  <small class="text-uppercase text-medium-emphasis">
                    <TeamName :teamId="entry.team_id" />
                  </small>
                </div>
                <!-- Remove from group -->
                <span v-if="editMode && standingsType != 'teams'">
                  <v-btn icon="mdi-close" size="x-small" variant="text" color="error"
                    @click="$emit('remove-from-group', entry)"
                    :title="$t('tournaments.removeFromGroup')"
                  ></v-btn>
                </span>
              </div>
            </div>

            <!-- Other columns -->
            <template v-else>
              <StandingsField v-if="isColumnsEditable(column.key)"
                :model-value="entry[column.key]"
                @update:modelValue="value => updateEntryColumn(entry, column.key, value)" />
              <template v-else>
                <span :class="{
                  'font-weight-bold': column.key === 'points',
                }">
                  {{ entry[column.key] }}
                </span>
              </template>
            </template>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
.table-container {
  overflow-x: auto;
}

.standings-table {
  color: #ddd;
}

.sticky-column {
  position: sticky;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1;
}

.sticky-column-first {
  left: 0;
  width: 36px;
}

.sticky-column-second {
  left: 0;
  min-width: 150px;
}

.sticky-column-second--desktop {
  left: 36px;
}

.sticky-column-second::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 1px;
  background-color: rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
}
</style>
